import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Routes } from '../../../app/Routes';
import { BlechconOrder } from '../../../blechcon';
import { Button } from '../../../components/Button/Button';
import { CenteredRing } from '../../../components/CenteredRing/CenteredRing';
import { formatDate } from '../../../components/helper';
import { Menu } from '../../../components/Menu/Menu';
import { Modal } from '../../../components/Modal';
import { Page } from '../../../components/Page/Page';
import { StatusBadge } from '../../../components/StatusBadge/StatusBadge';
import { localized } from '../../../config/localization';
import { fetchCustomerOrders, removeOrder } from '../../../services/api/blechconApi';
import { useSearchParams } from '../../../services/useSearchParams';
import { formatMeasure } from '../../../utils';
import './CustomerOrdersPage.css';
import { UserOrdersTabMenu } from './UserOrdersTabMenu';
import { useNavigate } from 'react-router';

export function CustomerOrdersPage(): JSX.Element {
  const { filter } = useSearchParams();

  const customerFilterActive = filter === 'customer';

  return (
    <Page title={localized.RECENT_ORDERS}>
      <CustomerOrdersListView key={customerFilterActive.toString()} customerFilterActive={customerFilterActive} />
    </Page>
  );
}

type Props = {
  readonly customerFilterActive: boolean;
};

function CustomerOrdersListView({ customerFilterActive }: Props): JSX.Element {
  const navigate = useNavigate();

  const abortControllerRef = useRef<AbortController | null>(null);

  const [showRemoveOrderModal, setShowRemoveOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<BlechconOrder | null>(null);

  const [state, setState] = useState({
    isFetching: false,
    orders: [] as BlechconOrder[],
    lastEvaluatedKey: null,
  });

  const fetchAllOrders = useCallback(
    async (lastEvaluatedKey = null, resetOrders = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setState((current) => ({
        ...current,
        isFetching: true,
      }));

      if (lastEvaluatedKey === undefined) {
        return;
      }

      try {
        const result = await fetchCustomerOrders(customerFilterActive, lastEvaluatedKey);

        if (!abortControllerRef.current.signal.aborted) {
          setState((current) => ({
            ...current,
            orders: resetOrders ? result.Items : [...current.orders, ...result.Items],
            lastEvaluatedKey: result.LastEvaluatedKey,
          }));
        }
      } catch (error) {
        if (!abortControllerRef.current.signal.aborted) {
          console.log(error);
          toast.error(String(error));
        }
      } finally {
        if (!abortControllerRef.current.signal.aborted) {
          setState((current) => ({
            ...current,
            isFetching: false,
          }));
        }
      }
    },
    [customerFilterActive]
  );

  useEffect(() => {
    fetchAllOrders(null, true);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchAllOrders]);

  const deleteOrder = useCallback(async () => {
    if (selectedOrder) {
      try {
        await removeOrder(selectedOrder.id);

        setState((current) => ({
          ...current,
          orders: current.orders.filter((it) => it.id !== selectedOrder?.id),
        }));

        setShowRemoveOrderModal(false);
        setSelectedOrder(null);
      } catch (error) {
        toast.error(String(error));
      }
    }
  }, [selectedOrder]);

  const onClick = (order: BlechconOrder) => {
    if (order.orderStatus === 'confirmed' || order.orderStatus === 'submitted' || order.orderStatus === 'withdraw') {
      navigate({
        pathname: Routes.customerOrdersDetails(order.id),
      });
    } else {
      navigate({
        pathname: Routes.checkoutStep(order.checkoutStep)(order.id),
      });
    }
  };

  return (
    <>
      {showRemoveOrderModal && (
        <Modal
          title={localized.REMOVE_OFFER}
          onCancel={{
            label: localized.ABORT,
            execute: () => {
              setSelectedOrder(null);
              setShowRemoveOrderModal(false);
            },
          }}
          onSuccess={{
            label: localized.REMOVE,
            execute: () => deleteOrder(),
          }}
        >
          <p className="modal-text">{localized.REMOVE_OFFER_CONFIRMATION}</p>
        </Modal>
      )}

      <UserOrdersTabMenu isCustomerFilterActive={customerFilterActive} />

      <table className="table">
        <thead>
          <tr>
            <td>{localized.OFFER_ID}</td>
            <td>{localized.DATE_ORDERED}</td>
            <td>{localized.CUSTOMER_REFERENCE}</td>
            <td>{localized.MATERIAL_COST}</td>
            <td>{localized.SHIPPING_COST}</td>
            <td>{localized.TOTAL}</td>
            <td>{localized.STATUS}</td>
            <td></td>
            <td />
          </tr>
        </thead>

        <tbody>
          {state.orders.map((order) => (
            <tr key={order.id} onClick={() => onClick(order)}>
              <td>{order.id}</td>
              <td>{formatDate(order.orderedAt)}</td>
              <td>{order.customerReference}</td>
              <td>{formatMeasure(order.prices.materialTotalPrice)}</td>
              <td>{formatMeasure(order.prices.shippingCost?.price)}</td>
              <td>{formatMeasure(order.prices.totalPrice)}</td>
              <td>
                <StatusBadge className="customer-orders-page__status-badge" orderStatus={order.orderStatus} />
              </td>
              <td
                onClick={(event) => {
                  // unterbinde onClick auf tr
                  event.stopPropagation();
                }}
              >
                {order.orderStatus === 'draft' && (
                  <Menu dots>
                    <Menu.Item
                      onSelect={() => {
                        setSelectedOrder(order);
                        setShowRemoveOrderModal(true);
                      }}
                    >
                      {localized.REMOVE_OFFER}
                    </Menu.Item>
                  </Menu>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {state.isFetching ? (
        <div className="customer-orders-page__centered-ring">
          <CenteredRing />
        </div>
      ) : null}

      {!state.isFetching && state.lastEvaluatedKey ? (
        <div className="customer_orders_page__load-more-button">
          <Button
            disabled={state.isFetching}
            onClick={() => {
              fetchAllOrders(state.lastEvaluatedKey);
            }}
            secondary
          >
            {localized.LOAD_MORE}
          </Button>
        </div>
      ) : null}
    </>
  );
}
